import { ContextConsumer, createContext } from "@lit/context";
import i18next from "i18next";
import { LitElement } from "lit";
import {
  EquirectangularReflectionMapping,
  LinearSRGBColorSpace,
  Scene,
  TextureLoader,
  Vector3,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EditorTransformController } from "../editor/EditorTransformController";
import { EngineCamera } from "../engine/camera/Camera";
import { Engine } from "../engine/Engine";
import { getEventPixeslOnElement } from "../event/EventUtil";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelConfigurator } from "../loader/SugarModelConfigurator";
import { FloatMenu } from "../ui/FloatMenu";
import { wait } from "../util/Wait";
import { logger } from "../util/Logger";
import {
  setConfiguratorData,
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
} from "./ConfiguratorData";
import { ConfiguratorController } from "./SugarConfiguratorController";
import { SugarConfiguratorEntity } from "./SugarConfiguratorEntity";

const FORWARDED_EVENTS = [
  //"click",
  "dblclick",
  /*  "pointerdown",
  "pointerup",
  "pointermove",*/
  "dragstart",
  //"dragover",
  //"dragend",
  //"drop",
  "keydown",
  "keyup",
  "input",
  "change",
  "submit",
  "focus",
  "blur",
  "delete",
  "replace",
  "rotate-change",
  "rotate-end",
  "model-selected",
  "model-deselected",
];

export const configuratorContext = createContext<SugarConfiguratorContext>(
  "configuratorContext"
);

export class SugarConfiguratorContext extends EventTarget {
  private _model?: CONFIGURATOR_MODEL_TYPE;
  private _locale = "TR";
  private _engine?: Engine;
  private _element: HTMLElement;
  private _configuratorEntity?: SugarConfiguratorEntity;
  private _configuratorId = "default";

  private _configuratorController!: ConfiguratorController;
  private _editorController!: EditorTransformController;
  private _orbitControls!: OrbitControls;

  private _floatMenu?: FloatMenu;
  private _floatMenuShowed = false;

  constructor(element: HTMLElement) {
    super();
    this._element = element;
    FORWARDED_EVENTS.forEach((eventName) => {
      this._element.addEventListener(eventName, (event: any) => {
        const clonedEvent = new event.constructor(event.type, event);
        this._engine?.dispatchEvent(clonedEvent);
      });
    });

    // Query string'den dil kodunu kontrol et
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lang");
    i18next.init(() => {
      if (lang === "en" || lang === "de" || lang === "tr") {
        i18next.changeLanguage(lang);
      } else {
        i18next.changeLanguage("tr");
      }
    });
  }

  start(bindElement: HTMLElement, type: "editor" | "configurator" = "editor") {
    if (!this._engine) {
      this._engine = new Engine();
    }

    const engine = this._engine!;
    engine.bindElement(bindElement);
    engine.setUseRenderSceneSwap(false);
    engine.setRenderSceneMergeMeshesPerMaterial(false);
    engine.setDebugLogRenderScene(false);
    engine.start();

    const activeCamera = new EngineCamera(); /*new PersCamera(
      25,
      this.engine!.width,
      this.engine!.height
    );*/

    this._orbitControls =
      activeCamera.controller as OrbitControls; /*new OrbitControls(
      activeCamera.instance,
      Engine?.activeEngine?.renderer.instance.domElement
    );*/

    // activeCamera.controller = this._orbitControls;

    //fro meter
    activeCamera.instance.userData.controller = this._orbitControls;
    activeCamera.instance.position.set(5, 5, 5);
    activeCamera.instance.lookAt(new Vector3(0, 0, 0));
    this.engine!.activeCamera = activeCamera;

    this._configuratorEntity = new SugarConfiguratorEntity(type);
    this._configuratorEntity.configuratorId = this._configuratorId;
    engine.addEntity(this._configuratorEntity);

    this._configuratorController = new ConfiguratorController(
      this._orbitControls!,
      this._configuratorId
    );
    Engine.activeEngine!.activeScene!.add(this._configuratorController);
    this.engine?.addEntity(this._configuratorController);

    this._editorController = new EditorTransformController(
      this._orbitControls!
    );
    this.engine?.addEntity(this._editorController);

    this.mode = type;

    const tLoader = new TextureLoader();
    tLoader
      .loadAsync(
        "https://img-sugartech.mncdn.com/cdn.sugartech/mottobucket/FFC6JX4TV8.png"
      )

      .then((hdrTexture) => {
        hdrTexture.mapping = EquirectangularReflectionMapping;
        this.engine!.activeScene!.environment = hdrTexture;

        hdrTexture.colorSpace = LinearSRGBColorSpace;
      });

    //refresh ui
    engine.addEventListener("model-added", () => {
      this.refresh = true;
    });

    engine.addEventListener("material-changed", () => {
      this.refresh = true;
    });

    engine.addEventListener("refresh-changed", () => {
      this.refresh = true;
    });

    engine.addEventListener("part-changed", () => {
      this.refresh = true;
    });

    engine.addEventListener("side-changed", () => {
      this.refresh = true;
    });

    engine.addEventListener("model-removed", () => {
      this.refresh = true;
    });

    engine.addEventListener("model-selected", (e: any) => {
      this.selectedModel = e.detail as SugarModel;
      this.refresh = true;

      if (!this._floatMenu) {
        this._floatMenu = document.createElement("float-menu") as FloatMenu;
      }
      if (e.detail) {
        if (this.selectedModel instanceof SugarModelConfigurator) {
          this._floatMenu.setAttribute("rotation", "true");
          this._floatMenu.setAttribute("delete", "true");
          this._floatMenu.setAttribute("replace", "false");
        } else {
          this._floatMenu.setAttribute("rotation", "false");
          this._floatMenu.setAttribute("delete", "true");
          this._floatMenu.setAttribute("replace", "true");
        }

        this._engine?.canvas?.parentElement?.append(this._floatMenu);
      }
    });

    engine.addEventListener("model-deselected", (detail) => {
      this.selectedModel = undefined;
      this.refresh = true;

      if (this._floatMenu) {
        this._floatMenu?.remove();
        this._floatMenu = undefined;
        this._floatMenuShowed = false;
      }
    });

    engine.addEventListener("scene-changed", (e: any) => {
      this.scene = e.detail;
    });

    bindElement.addEventListener("click", (e: MouseEvent | TouchEvent) => {
      if (this._floatMenu && !this._floatMenuShowed) {
        const { x, y } = getEventPixeslOnElement(e);
        const { width, height } = this._floatMenu?.getBoundingClientRect();
        this._floatMenu!.style.left = x - width * 0.5 + "px";
        this._floatMenu!.style.top = y - height * 0.5 + "px";
        this._floatMenuShowed = true;
      }
    });
  }

  set configuratorId(value: string) {
    this._configuratorId = value;
    if (this._configuratorEntity) {
      this._configuratorEntity.configuratorId = value;
    }
  }

  get configuratorId(): string {
    return this._configuratorId;
  }

  set refresh(value: any) {
    this.dispatchEvent(new CustomEvent("refresh-changed"));
  }

  set model(value: CONFIGURATOR_MODEL_TYPE) {
    this._model = value;

    setConfiguratorData(this._configuratorId, value);

    this.dispatchEvent(new CustomEvent("model-changed"));
  }

  get model(): CONFIGURATOR_MODEL_TYPE {
    return this._model!;
  }

  get engine(): Engine | undefined {
    return this._engine;
  }

  private _type: "editor" | "configurator" | "both" = "configurator";
  set mode(value: "configurator" | "editor" | "both") {
    this._type = value;
    this._configuratorController.enabled =
      this._type === "configurator" || this._type === "both";

    this._editorController.enabled =
      this._type === "editor" || this._type === "both";
  }

  private _scene?: any;

  set scene(value: any) {
    this._scene = value;
    this.dispatchEvent(new CustomEvent("scene-changed", { detail: value }));
  }

  get scene() {
    return this._scene;
  }

  private _sugarModel?: SugarModel;
  set selectedModel(value: SugarModel | undefined) {
    this._sugarModel = value;
    this.dispatchEvent(
      new CustomEvent("selectedModel-changed", { detail: this._sugarModel })
    );
  }

  get selectedModel(): SugarModel | undefined {
    return this._sugarModel;
  }

  async changeMaterial(materialConfig: any[]) {
    const map = new Map<string, { code: string; material: any }>();
    materialConfig.forEach((m) => map.set(m.code, m));
    this._configuratorController.setMaterial(map);
    await this._configuratorController.applyMaterial();
    this.engine?.dispatchCustomEvent("scene-changed");
  }

  async changePart(partConfig: MODEL_PART_TYPE) {
    this._configuratorController.setMainPart(partConfig);
    await this._configuratorController.changeMainPart();
    await this._configuratorController.applyMaterial();
    this.engine?.dispatchCustomEvent("scene-changed");
  }

  async changeSidePart(sideConfig: MODEL_PART_TYPE) {
    this._configuratorController.setSidePart(sideConfig);
    await this._configuratorController.changeSidePart();
    await this._configuratorController.applyMaterial();
    this.engine?.dispatchCustomEvent("scene-changed");
  }

  async setTopViewForRender(): Promise<void> {
    await wait(100);
    this._engine?.dispatchCustomEvent("showTopView", { detail: true });
    this._engine?.dispatchCustomEvent("showDimensions", { detail: true });
  }

  async takeScreenshot(): Promise<string | null> {
    const blob = await this._engine?.renderer.screenshot("image/png", 1);
    if (blob) return URL.createObjectURL(blob);

    return null;
  }

  async takeThumbnnail(): Promise<string | null> {
    const scene = new Scene();
    /*const blob = await this._engine?.renderer.render()
    if (blob) return URL.createObjectURL(blob);*/

    return null;
  }

  clearAll() {
    //this._engine?.dispatchCustomEvent("clear");
    this._configuratorController.removeAll();
    //this._selectedModel = undefined;
    this.engine?.dispatchCustomEvent("model-deselected");
  }

  showTopView() {
    this._engine?.dispatchCustomEvent("showTopView", { detail: false });
  }

  showDimensions() {
    this._engine?.dispatchCustomEvent("showDimensions");
  }

  changeMode(mode: any) {
    //this._configuratorEntity!.mode = mode;
    //this._engine?.dispatchCustomEvent("mode-changed", { detail: mode });
    this.mode = mode;
  }

  get products() {
    const models = this._configuratorController?.configurators.flatMap(
      (m) => m.products
    );
    return models;
  }

  validateConfigurations() {
    let valid = true;
    this.products.forEach((product) => {
      product.forEach((p) => {
        //klem köse
        if (p?.userData.info.id === 60344 || p?.userData.info.id === 87826) {
          p.joints?.forEach((j) => {
            if ((j.inType === "sol" || j.inType === "sag") && !j.binded) {
              valid = false;
              logger.info("validation false");
            }
          });
        }
      });
    });

    return valid;
  }
}

/*****
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function observeContext(
  ...properties: (keyof SugarConfiguratorContext)[]
) {
  const eventNames = properties.map((property) => `${property}-changed`);
  const onProperyChangedNames = properties.map(
    (property) =>
      `on${property.charAt(0).toUpperCase() + property.slice(1)}Changed`
  );

  return function <T extends { new (...args: any[]): LitElement }>(Base: T) {
    return class extends Base {
      private __contextConsumer = new ContextConsumer(this, {
        context: configuratorContext,
        callback: (value) => {
          if ((this as any)["onContextChanged"]) {
            (this as any)["onContextChanged"](value);
          }
          this.__context = value;
          this.__setupContextListener();
          properties.forEach((property, i) => {
            if ((this.__context as any)?.[property] !== undefined) {
              this.__onContextChange(onProperyChangedNames[i], property);
            }
          });
        },
      });

      __context?: SugarConfiguratorContext;
      private __onContextChanges: Map<
        String,
        EventListenerOrEventListenerObject
      > = new Map();

      private __setupContextListener() {
        eventNames.forEach((eventName, i) => {
          if (this.__onContextChanges.has(eventName))
            this.__context?.removeEventListener(
              eventName,
              this.__onContextChanges.get(eventName)!
            );

          const c = () => {
            this.__onContextChange(onProperyChangedNames[i], properties[i]);
          };

          this.__context?.addEventListener(eventName, c);
          this.__onContextChanges.set(eventName, c);
        });
      }

      private __onContextChange = (
        onProperyChanged: string,
        property: string
      ) => {
        if ((this as any)[onProperyChanged]) {
          (this as any)[onProperyChanged]((this.__context as any)?.[property]);
        }
      };

      connectedCallback() {
        super.connectedCallback?.();
        this.__setupContextListener();
      }

      disconnectedCallback() {
        super.disconnectedCallback?.();
        eventNames.forEach((eventName, i) => {
          if (this.__onContextChanges.has(eventName))
            this.__context?.removeEventListener(
              eventName,
              this.__onContextChanges.get(eventName)!
            );
        });
      }

      protected get context(): SugarConfiguratorContext | undefined {
        return this.__context;
      }
    };
  };
}

export function injectContext(): PropertyDecorator {
  return (proto: any, key: string | symbol) => {
    Object.defineProperty(proto, key, {
      get() {
        return configuratorContext;
      },
      enumerable: true,
      configurable: true,
    });
  };
}

interface WithEventHandlerTracking {
  __eventHandlers?: {
    type: string;
    handler: EventListener;
    target: EventTarget;
  }[];
}

type HTMLElementWithLifecycle = HTMLElement &
  Partial<WithEventHandlerTracking> & {
    connectedCallback?(): void;
    disconnectedCallback?(): void;
  };

export function onEvent<K extends keyof HTMLElementEventMap>(
  type: K,
  options?: { target?: EventTarget }
): MethodDecorator {
  return function (
    target: Object,
    key: string | symbol,
    _descriptor: PropertyDescriptor
  ) {
    const connected = (target as HTMLElementWithLifecycle).connectedCallback;
    const disconnected = (target as HTMLElementWithLifecycle)
      .disconnectedCallback;

    (target as HTMLElementWithLifecycle).connectedCallback = function () {
      const self = this as HTMLElementWithLifecycle;
      const handler = (e: Event) => (self as any)[key](e);
      const eventTarget = options?.target ?? self;

      eventTarget.addEventListener(type, handler);

      self.__eventHandlers ??= [];
      self.__eventHandlers.push({ type, handler, target: eventTarget });

      connected?.call(self);
    };

    (target as HTMLElementWithLifecycle).disconnectedCallback = function () {
      const self = this as HTMLElementWithLifecycle;
      for (const { type, handler, target } of self.__eventHandlers ?? []) {
        target.removeEventListener(type, handler);
      }
      self.__eventHandlers = [];

      disconnected?.call(self);
    };
  };
}
